"use client";

import React, { useEffect, useId, useState } from "react";
import { Control, useController } from "react-hook-form";

import { Label } from "@/components/ui/label";

import { cx } from "@/lib/tailwind-utils";

import { Checkbox } from "./checkbox";
import Input from "./input";
import { RadioGroup, RadioGroupItem } from "./radio-group";

interface Option {
  label: string;
  value: string;
}

interface CheckboxSelectProps {
  name: string;
  control: Control<any>;
  options: Option[];
  columns?: number;
  multiselect?: boolean;
  className?: string;
  hasError?: boolean;
  returnListForSingleSelect?: boolean;
  enableCustomOther?: boolean;
  disabled?: boolean;
  /** Single select only: clicking the chosen option again clears it. */
  allowDeselect?: boolean;
}

export const CheckboxSelect: React.FC<CheckboxSelectProps> = ({
  name,
  control,
  options,
  columns = 2,
  multiselect = false,
  className,
  hasError = false,
  returnListForSingleSelect = true,
  enableCustomOther = false,
  disabled = false,
  allowDeselect = false,
}) => {
  const idPrefix = useId();
  const { field } = useController({
    name,
    control,
    defaultValue: multiselect ? [] : returnListForSingleSelect ? [] : "",
  });

  // For "Other" option
  const [otherValue, setOtherValue] = useState("");

  // Find the "Other" option if it exists
  const otherOption = options.find(
    (opt) => opt.label.toLowerCase() === "other",
  );

  // Check if "Other" is selected
  const isOtherSelected = multiselect
    ? otherOption &&
      (field.value?.includes(otherOption.value) ||
        field.value?.some((v: string) => v.startsWith("other: ")))
    : (otherOption && field.value === otherOption.value) ||
      (typeof field.value === "string" && field.value.startsWith("other: ")) ||
      (returnListForSingleSelect &&
        Array.isArray(field.value) &&
        field.value[0] &&
        typeof field.value[0] === "string" &&
        field.value[0].startsWith("other: "));

  // Check if we have a custom value
  const hasCustomValue = multiselect
    ? Array.isArray(field.value) &&
      field.value.some((v: string) => v.startsWith("other: "))
    : (returnListForSingleSelect && field.value?.[0]?.startsWith("other: ")) ||
      (!returnListForSingleSelect &&
        typeof field.value === "string" &&
        field.value.startsWith("other: "));

  // Initialize "Other" text value if present
  useEffect(() => {
    if (hasCustomValue) {
      if (multiselect) {
        const customEntry = field.value.find((v: string) =>
          v.startsWith("other: "),
        );
        if (customEntry) setOtherValue(customEntry.substring(7));
      } else {
        const customValue = returnListForSingleSelect
          ? field.value[0].substring(7) // Remove "other: " prefix
          : field.value.substring(7);
        setOtherValue(customValue);
      }
    } else if (isOtherSelected && !otherValue) {
      // "Other" is selected but no custom value yet
      setOtherValue("");
    }
  }, [field.value]);

  const radioValue =
    otherOption &&
    ((typeof field.value === "string" && field.value.startsWith("other:")) ||
      (returnListForSingleSelect &&
        Array.isArray(field.value) &&
        field.value[0] &&
        typeof field.value[0] === "string" &&
        field.value[0].startsWith("other:")))
      ? otherOption.value // Use "Other" value when we have a custom value
      : returnListForSingleSelect
        ? field.value?.[0] || ""
        : field.value || "";

  const handleCheckboxChange = (value: string) => {
    let newSelectedValues: string[] | string;

    // Find if the value is the "Other" option
    const isOtherOptionValue = otherOption && value === otherOption.value;

    if (multiselect) {
      if (isOtherOptionValue && enableCustomOther) {
        if (isOtherSelected) {
          // Deselecting "Other" — remove it and clear custom value
          newSelectedValues = field.value.filter(
            (v: string) => v !== value && !v.startsWith("other: "),
          );
          setOtherValue("");
        } else {
          // Selecting "Other" — use custom value if we have one
          newSelectedValues = otherValue
            ? [...field.value, `other: ${otherValue}`]
            : [...field.value, value];
        }
      } else {
        newSelectedValues = field.value.includes(value)
          ? field.value.filter((v: string) => v !== value)
          : [...field.value, value];
      }
    } else {
      // For single-select radio buttons
      if (isOtherOptionValue && enableCustomOther) {
        // If selecting "Other" and we already have a custom value
        if (otherValue) {
          newSelectedValues = returnListForSingleSelect
            ? [`other: ${otherValue}`]
            : `other: ${otherValue}`;
        } else {
          // No custom value yet, just select "Other"
          newSelectedValues = returnListForSingleSelect ? [value] : value;
        }
      } else {
        // For regular options, clear other value
        setOtherValue("");
        newSelectedValues = returnListForSingleSelect ? [value] : value;
      }
    }

    field.onChange(newSelectedValues);
  };

  // Handle changes to the "Other" input field
  const handleOtherValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newOtherValue = e.target.value;
    setOtherValue(newOtherValue);

    if (otherOption) {
      if (multiselect) {
        // Replace the raw "Other" value or previous custom value with the new one
        const filtered = field.value.filter(
          (v: string) => v !== otherOption.value && !v.startsWith("other: "),
        );
        field.onChange([
          ...filtered,
          newOtherValue ? `other: ${newOtherValue}` : otherOption.value,
        ]);
      } else {
        // For radio buttons, update with the custom value
        if (newOtherValue) {
          const newValue = `other: ${newOtherValue}`;
          if (returnListForSingleSelect) {
            field.onChange([newValue]);
          } else {
            field.onChange(newValue);
          }
        } else {
          // If they cleared the input, revert to just "Other"
          if (returnListForSingleSelect) {
            field.onChange([otherOption.value]);
          } else {
            field.onChange(otherOption.value);
          }
        }
      }
    }
  };

  return (
    <div className={cx("space-y-2", className)}>
      {multiselect && (
        <p className="-mt-0.5 text-caption text-muted-foreground">
          Please check all that apply
        </p>
      )}
      <div
        className={cx(
          `grid grid-cols-${columns} gap-2 rounded-md border`,
          hasError ? "border-status-error-text" : "border-transparent",
        )}
      >
        {multiselect ? (
          options.map((option) => (
            <div
              key={option.value}
              className="flex w-fit cursor-pointer items-center space-x-2"
            >
              <Checkbox
                checked={
                  otherOption && option.value === otherOption.value
                    ? isOtherSelected
                    : field.value?.includes(option.value)
                }
                onCheckedChange={() => handleCheckboxChange(option.value)}
                disabled={disabled}
              />
              <Label
                className="cursor-pointer text-body"
                onClick={() => handleCheckboxChange(option.value)}
              >
                {option.label}
              </Label>
            </div>
          ))
        ) : (
          <RadioGroup
            disabled={disabled}
            value={radioValue}
            onValueChange={(value) => handleCheckboxChange(value)}
          >
            {options.map((option) => (
              <div key={option.value} className="flex items-center space-x-1">
                <RadioGroupItem
                  value={option.value}
                  id={`${idPrefix}-${option.value}`}
                  // Radix never unchecks a radio, so clicking the chosen one
                  // (or Space on it) clears the answer here.
                  onClick={() => {
                    if (allowDeselect && option.value === radioValue) {
                      setOtherValue("");
                      field.onChange(returnListForSingleSelect ? [] : "");
                    }
                  }}
                />
                <Label
                  htmlFor={`${idPrefix}-${option.value}`}
                  className="cursor-pointer text-body"
                  disabled={disabled}
                >
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        )}
      </div>

      {/* Show text input for "Other" option when selected */}
      {enableCustomOther && isOtherSelected && (
        <div className="mt-2">
          <Input
            placeholder="Please specify other value"
            value={otherValue}
            onChange={handleOtherValueChange}
            disabled={disabled}
            className={cx(hasError && "border-status-error-bg")}
          />
        </div>
      )}
    </div>
  );
};
